import Vue from 'vue';
import VueRouter from 'vue-router';
import { loadLanguageAsync } from '../plugins/i18n';
import store from './../store/store';
import visitTracker from '../utils/visitTracker';

// Route groups
import AuthRoutes from './auth';
import CheckoutRoutes from './checkout';
import HomeRoutes from './home';
import PageRoutes from './page';
import ShopRoutes from './shop';
import MypageRoutes from './mypage';
import EmbedRoutes from './embed';
import HelpdeskRoutes from './helpdesk';
import GuideRoutes from './guide';
import FileboxRoutes from './filebox';
import EventRoutes from './event';
import AfterLoginRoutes from './after_login';

const Error404 = () => import('../pages/errors/404.vue');

Vue.use(VueRouter);

/**
 * VueRouter.prototype.push / replace 패치
 * NavigationDuplicated · NavigationRedirected 에러가 v-on 핸들러에서
 * unhandled rejection → [Vue warn] 으로 노출되는 것을 전역 차단.
 * router.onError() 는 router 내부 에러만 잡고, 컴포넌트가 직접 호출한
 * this.$router.push() Promise 거부는 여기서만 막을 수 있다.
 */
function suppressNavError(err) {
    if (
        err.name === 'NavigationDuplicated' ||
        err.name === 'NavigationCancelled' ||
        err.name === 'NavigationRedirected' ||
        err.message?.includes('Redirected when going from') ||
        err.message?.includes('via a navigation guard')
    ) {
        return;
    }
    return Promise.reject(err);
}
const _push = VueRouter.prototype.push;
VueRouter.prototype.push = function (location, onResolve, onReject) {
    if (onResolve || onReject) return _push.call(this, location, onResolve, onReject);
    return _push.call(this, location).catch(suppressNavError);
};
const _replace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function (location, onResolve, onReject) {
    if (onResolve || onReject) return _replace.call(this, location, onResolve, onReject);
    return _replace.call(this, location).catch(suppressNavError);
};

const routes = [
    ...AfterLoginRoutes,
    ...HomeRoutes, //
    ...CheckoutRoutes,
    ...AuthRoutes,
    ...MypageRoutes,
    ...EmbedRoutes,
    ...HelpdeskRoutes,
    ...GuideRoutes,
    ...FileboxRoutes,
    ...EventRoutes, // PageRoutes 보다 앞에 위치해야 catch-all(/:pageSlug?)에 먹히지 않음
    // /404 정적 라우트는 ShopRoutes(:slug 동적 세그먼트)보다 반드시 앞에 위치해야 한다.
    // ShopRoutes가 먼저 등록되면 /:slug 가 /404 를 shop slug=404 로 매칭해버린다.
    { path: '/404', component: Error404, name: '404', meta: { requiresAuth: false } },
    ...ShopRoutes, // PageRoutes 보다 앞에 위치해야 catch-all(/:pageSlug?)에 먹히지 않음
    ...PageRoutes,
];
const router = new VueRouter({
    mode: 'history',
    base: '/',
    scrollBehavior: () => ({ x: 0, y: 0 }),
    routes: routes,
});

/**
 * 라우터 에러 핸들러
 */
router.onError((error) => {
    // 네비게이션 취소, 중복, 리다이렉트 관련 에러는 무시
    if (
        error.name === 'NavigationCancelled' ||
        error.name === 'NavigationDuplicated' ||
        error.message?.includes('Redirected when going from') ||
        error.message?.includes('via a navigation guard')
    ) {
        return;
    }
    console.error('Router error:', error);
});

/**
 * 네비게이션 가드 - 라우트 이동 전 실행
 * @param {Route} to - 이동하려는 목표 라우트
 * @param {Route} from - 현재 라우트
 * @param {Function} next - 다음 네비게이션을 진행하기 위한 콜백 함수
 */
router.beforeEach(async (to, from, next) => {
    try {
        // ConversationsDetails 페이지에서 나갈 때 인터벌 정리
        if (from.name === 'ConversationsDetails' && window.intervalCall) {
            clearInterval(window.intervalCall);
        }

        // 라우터 로딩 상태 시작
        store.commit('app/setRouterLoading', true);

        // 소셜 로그인 상태 처리
        if (to.query.social_login === 'failed') {
            store.commit('auth/setSociaLoginStatus', 'failed');
        } else if (to.query.access_token) {
            store.commit('auth/setAccessToken', to.query.access_token);
            store.commit('auth/setSociaLoginStatus', 'success');
        }

        // 사용자 언어 설정 검증
        const locale = store.getters['app/userLanguage'];
        const allLocales = store.getters['app/allLanguages'];
        if (!allLocales.find((lang) => lang.code === locale)) {
            store.dispatch('app/removeLanguage');
        }

        // 언어 비동기 로드
        await loadLanguageAsync(locale);

        // 인증 상태 확인
        const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
        const isAuthenticated = store.getters['auth/isAuthenticated'];

        // 인증이 필요한 페이지인데 인증되지 않은 경우
        if (requiresAuth && !isAuthenticated) {
            if (from.name === 'Login') {
                store.commit('auth/updateCartDrawer', false);
                store.commit('auth/updateMobileSideMenu', false);
            }
            // shop 컨텍스트 전파: 목표 → 출발 라우트 순서로 slug 탐색
            const loginQuery = { redirect: to.fullPath, _: Date.now() };
            const shopSlug =
                to.params.slug ||
                from.params.slug ||
                from.query.shop_id ||
                (from.query.redirect || '').split('/').filter(Boolean)[0];
            if (shopSlug) {
                loginQuery.shop_id = shopSlug;
            }
            // 리다이렉트 시 에러를 무시하도록 처리
            const nextResult = next({
                name: 'Login',
                query: loginQuery,
            });
            // next()가 Promise를 반환하는 경우에만 catch 처리
            if (nextResult && typeof nextResult.catch === 'function') {
                nextResult.catch((err) => {
                    // 네비게이션 가드 리다이렉트 에러는 무시
                    if (
                        err.name !== 'NavigationCancelled' && //
                        err.name !== 'NavigationDuplicated' &&
                        !err.message?.includes('Redirected when going from')
                    ) {
                        console.error('Navigation redirect error:', err);
                    }
                });
            }
            return;
        }

        // 이미 인증된 사용자가 인증 페이지에 접근하는 경우
        const authPages = ['Login', 'Registration', 'ForgotPassword'];
        if (isAuthenticated && authPages.includes(to.name)) {
            return next({ name: 'AfterLogin', _: Date.now() });
        }

        // 홈(/) 접근 시 샵 페이지로 리다이렉트
        if (to.name === 'Home') {
            if (isAuthenticated) {
                const shopSlug = store.getters['auth/currentUserShopSlug'];
                if (shopSlug) {
                    return next({ path: `/${shopSlug}`, replace: true });
                }
                // shopSlug 미로드 상태(getUser 완료 전) → Home 그대로 통과
            } else {
                const defaultShopSlug = window.shopSetting?.defaultShopSlug;
                if (defaultShopSlug) {
                    return next({ path: `/${defaultShopSlug}`, replace: true });
                }
            }
        }

        // 정상적으로 네비게이션 진행
        return next();
    } catch (error) {
        console.error('Navigation error:', error);
        store.commit('app/setRouterLoading', false);
        next(false);
    }
});

/**
 * 네비게이션 가드 - 라우트 이동 후 실행
 */
router.afterEach((to, from) => {
    store.commit('app/setRouterLoading', false);
    visitTracker.onPageChange(to, from);
});

export default router;
